// ---- Worn gear + held equip (server-owned paperdoll, client HUD mirror) ----
// Body wear (head/torso/legs/feet/back) plus the three held channels (main hand
// weapon, offhand shield, tool) as a humanoid paperdoll above the bag — all
// square cells, CSS grid by data-slot. Same Step-2 discipline as the bag
// (src/inventory.ts): the SERVER owns wear (index.ts `wear` handler) and equip
// (equip / equip-tool handlers); this module is a render mirror. Wear clicks
// send unwear intents; equip-slot clicks clear the held channel via inventory
// helpers. Wearing/equipping still happens from the bag side (Wear / Equip).
//
// Every accepted wear change re-dresses the local rig in place (player.ts
// redressPlayer — recolors + attached part geometry), so what you wear is what
// you see; remotes get the same treatment from their snapshot diff (net.ts).

import { $ } from "./dom.ts";
import { game } from "./state.ts";
import { itemSpec } from "./items/index.ts";
import { redressPlayer } from "./player.ts";
import {
  equippedType, equippedShieldType, equippedTool,
  unequipMain, unequipOffhand, unequipTool,
  setPaperdollHook,
} from "./inventory.ts";
import {
  DEFAULT_APPEARANCE,
  type Appearance, type Gear, type WearSlot,
} from "./shared.ts";
import { setPortraitGear } from "./hud.ts";

/** Held / equip channels shown on the paperdoll beside the body wear slots. */
const EQUIP_SLOTS = ["main", "off", "tool"] as const;
type EquipSlot = (typeof EQUIP_SLOTS)[number];

/** Dim placeholder glyph shown in an EMPTY slot (reads as "what goes here"). */
const WEAR_GLYPHS: Record<WearSlot, string> = {
  head: "🎩", torso: "👕", legs: "👖", feet: "🥾", back: "🎒",
};
const EQUIP_GLYPHS: Record<EquipSlot, string> = {
  main: "⚔", off: "🛡", tool: "🔧",
};
const EQUIP_LABEL: Record<EquipSlot, string> = {
  main: "Main hand", off: "Off hand", tool: "Tool",
};

/** DOM order must match CSS grid-area assignment (data-slot). Wear first so
 *  WEAR_SLOTS index math in older callers stays irrelevant — we key by data-slot. */
const PAPERDOLL: Array<{ slot: WearSlot; kind: "wear" } | { slot: EquipSlot; kind: "equip" }> = [
  { slot: "head", kind: "wear" },
  { slot: "main", kind: "equip" },
  { slot: "torso", kind: "wear" },
  { slot: "off", kind: "equip" },
  { slot: "back", kind: "wear" },
  { slot: "legs", kind: "wear" },
  { slot: "tool", kind: "equip" },
  { slot: "feet", kind: "wear" },
];

let gear: Gear = {};
let look: Appearance = { ...DEFAULT_APPEARANCE };
let wrap: HTMLElement;
let onUnwear: (slot: WearSlot) => void = () => {};

export function initGear(unwearHandler: (slot: WearSlot) => void): void {
  onUnwear = unwearHandler;
  wrap = $<HTMLElement>("#gearslots");
  for (const { slot, kind } of PAPERDOLL) {
    const cell = document.createElement("button");
    cell.className = "gearslot";
    cell.type = "button";
    cell.dataset.slot = slot; // CSS places each cell by body / hand area
    cell.dataset.kind = kind;
    cell.setAttribute("aria-label", kind === "wear" ? slot : EQUIP_LABEL[slot]);
    cell.addEventListener("click", () => {
      if (kind === "wear") {
        // Click a worn piece to take it off (intent — the server validates bag room
        // and echoes the new gear + bag; the caller gates on a free slot for UX).
        if (gear[slot]) onUnwear(slot);
        return;
      }
      // Equip channels stay in the bag while "equipped" — unequip is just clearing
      // the hand (no bag-room gate). inventory helpers fire the same intents as
      // the bag Equip button's Unequip path.
      if (slot === "main" && equippedType()) unequipMain();
      else if (slot === "off" && equippedShieldType()) unequipOffhand();
      else if (slot === "tool" && equippedTool()) unequipTool();
    });
    wrap.appendChild(cell);
  }
  // Bag equip/unequip re-renders the inventory HUD; keep the paperdoll in sync.
  setPaperdollHook(render);
  render();
}

/** Re-paint wear + equip cells (also hooked from inventory.ts on bag equip changes). */
export function refreshPaperdoll(): void {
  render();
}

/** The currently worn record (client mirror — UX hints only). */
export function wornGear(): Gear {
  return gear;
}

/** Initial load on join / reconnect (the `spawn` message): adopt the saved gear
 *  + the hero's look (kept for later re-dresses), and dress the rig to match. */
export function hydrateGear(g: Gear, lk: Appearance): void {
  look = { ...lk };
  setGear(g);
}

/** Mirror an authoritative gear record from the server (`gear` message) onto
 *  the HUD + the local rig. */
export function setGear(g: Gear): void {
  gear = { ...g };
  if (game.player) {
    const t = equippedType();
    redressPlayer(game.player, look, gear, t ? itemSpec(t) : null);
  }
  // Keep the bottom-left headshot in sync with worn kit (same 3D path as focus).
  setPortraitGear(gear);
  render();
}

function render(): void {
  if (!wrap) return;
  for (const cell of wrap.children as HTMLCollectionOf<HTMLElement>) {
    const slot = cell.dataset.slot;
    const kind = cell.dataset.kind;
    if (!slot || !kind) continue;

    if (kind === "wear") {
      const ws = slot as WearSlot;
      const type = gear[ws];
      cell.textContent = type ? itemSpec(type).icon : WEAR_GLYPHS[ws];
      cell.title = type ? `${itemSpec(type).name} — click to take off` : ws;
      cell.classList.toggle("worn", !!type);
      continue;
    }

    const es = slot as EquipSlot;
    const type =
      es === "main" ? equippedType()
      : es === "off" ? equippedShieldType()
      : equippedTool();
    cell.textContent = type ? itemSpec(type).icon : EQUIP_GLYPHS[es];
    cell.title = type
      ? `${EQUIP_LABEL[es]}: ${itemSpec(type).name} — click to unequip`
      : `${EQUIP_LABEL[es]} (empty)`;
    cell.classList.toggle("worn", !!type);
  }
}
